import { Fragment, memo } from 'react';
import { css } from 'twin.macro';
import { MdCancel } from 'react-icons/md';
import { BsBriefcase } from 'react-icons/bs';
import { LoadingSpinner } from '@/_elements/Loaders';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';

const eventTypeIcon = css`
    width: 80px;
    height: 80px;
    border-radius: 50%;
`;

const activeEventTypeRemove = css`
    position: absolute;
    top: -10px;
    right: -10px;
    z-index: 10 !important;
    display: flex;
    justify-content: end;

    svg {
        fill: #ed0000b8 !important;
        height: 30px;
        width: 30px;
    }
`;

const Component = memo(
    ({
        task,
        index,
        isBusy = false,
        showModal = null,
        imageOnly,
        activeEvent = {},
        nowTick = Date.now(),
        onEventClick = () => {},
        onRemoveActive = () => {},
    }) => {
        const eventTitle = task?.title || '';
        const eventTypeImage = task?.eventType ? task.eventType.image : '';
        const eventTypeName = task?.eventType ? task.eventType.title : '';
        const taskId = task?._id || task?.id;
        const startAt = new Date(task?.latestDatePointer || task?.date);
        const endAt = new Date(task?.latestDueDatePointer || task?.dueDate || task?.latestDatePointer || task?.date);

        const durationMs = Math.max(0, endAt - startAt);
        const progressRaw = durationMs > 0 ? (nowTick - startAt) / durationMs : 0;
        const progress = Math.max(0, Math.min(1, progressRaw));
        const isOngoing = progress > 0 && progress < 1;
        const progressDeg = Math.round(progress * 360);

        const handleClick = (event) => {
            event.stopPropagation();
            if (isBusy) return;
            onEventClick?.(task);
        };

        const handleRemove = (event) => {
            event.stopPropagation();
            onRemoveActive?.();
        };

        if (imageOnly) {
            return (
                <div
                    key={taskId || index}
                    css={[eventTypeIcon, isBusy && { backgroundColor: '#8ee0e2', width: 64, height: 64 }]}
                >
                    {eventTypeImage ? (
                        <img
                            src={eventTypeImage}
                            alt={eventTypeName}
                            css={[
                                eventTypeIcon,
                                isOngoing &&
                                    css`
                                        width: 64px;
                                        height: 64px;

                                        ${isOngoing ? 'border: 1px solid #ccc;' : ''}
                                    `,
                            ]}
                        />
                    ) : (
                        <div
                            css={css`
                                position: relative;
                                width: ${isOngoing ? '64px' : '80px'};
                                height: ${isOngoing ? '64px' : '80px'};
                                border-radius: 50%;
                                background: #fff;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                ${isOngoing ? 'border: 1px solid #ccc;' : ''}
                            `}
                        >
                            <BsBriefcase size={36} color="#334155" />
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div
                key={taskId || index}
                css={[
                    css`
                        width: 80px;
                        height: 80px;
                        border-radius: 50%;
                        justify-content: center;
                        padding: ${isOngoing ? '8px' : '0px'};
                        background: ${isOngoing
                            ? `conic-gradient(#0ea5e9 0deg, #0ea5e9 ${progressDeg}deg, white ${progressDeg}deg, white 360deg)`
                            : ''};
                        margin-left: auto;
                        margin-right: auto;
                        cursor: pointer;
                        position: relative;
                        box-shadow: rgba(100, 100, 111, 0.3) 0px 7px 29px 0px;
                        &:hover {
                            box-shadow: rgba(100, 100, 111, 0.9) 0px 7px 29px 0px;
                        }
                    `,
                    isBusy && { backgroundColor: '#8ee0e2' },
                ]}
                onClick={handleClick}
                title={`${eventTitle}\n${new Date(task?.latestDatePointer).toLocaleString()}`}
            >
                {isBusy && taskId === showModal ? (
                    <div>
                        <LoadingSpinner size={48} height={48} />
                    </div>
                ) : (
                    <Fragment>
                        {eventTypeImage ? (
                            <img
                                src={eventTypeImage}
                                alt={eventTypeName}
                                css={[
                                    eventTypeIcon,
                                    isOngoing &&
                                        css`
                                            width: 64px;
                                            height: 64px;

                                            ${isOngoing ? 'border: 1px solid #ccc;' : ''}
                                        `,
                                ]}
                            />
                        ) : (
                            <div
                                css={css`
                                    position: relative;
                                    width: ${isOngoing ? '64px' : '80px'};
                                    height: ${isOngoing ? '64px' : '80px'};
                                    border-radius: 50%;
                                    background: #fff;
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    ${isOngoing ? 'border: 1px solid #ccc;' : ''}
                                `}
                                title={`${eventTitle}\n${new Date(task?.latestDatePointer).toLocaleString()}`}
                            >
                                <BsBriefcase size={36} color="#334155" />
                            </div>
                        )}
                    </Fragment>
                )}

                {activeEvent?._id === taskId && (
                    <div css={activeEventTypeRemove} onClick={handleRemove}>
                        <MdCancel />
                    </div>
                )}
            </div>
        );
    }
);
const EventTypePreview = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default EventTypePreview;
